import React, {useContext, useMemo, useRef, useState} from 'react';
import Ipic from './Ipic.jsx';
import SymbolButton from '@bonprix/pattern-library/templates/symbol-button/default';
import Device from '@core/scripts/components/device';
import {Constants} from '../view-constants';
import {articleOverviewStaticContext} from "../ArticleOverviewStatic.jsx";
import {useBreakpointUp} from "bonprix-design-system";

function AlpiIpicSlider({firstViewColors, activeIndex, shopId, channelId, onChangeItem, inView}) {
    const breakpointXlUp = useBreakpointUp('xl')
    const {texts} = useContext(articleOverviewStaticContext);

    const [index, setIndex] = useState(0);
    const [touchPositionX, setTouchPositionX] = useState(0);
    const sliderRef = useRef();
    const currentSlidePosition = useRef(0);

    const isTouch = Device.isTouch();
    const ipicCount = firstViewColors.length; // TODO: multiple ipic lengths will be possible in the future

    const currentXPosition = useMemo(() => {
        if (!sliderRef.current) {
            return 0;
        }
        const slideWidth = getAlpiColorSlideWidth();
        const slideMargin = getAlpiColorSlideMargin();
        if (index % 1) {
            return (index * slideWidth - slideMargin / 2) * -1; // for an index = x.5 subtract half a margin, otherwise we slide too far
        }

        return index * slideWidth * -1;
    }, [index]);

    const isLeftButton = !isTouch && index > 0;
    const isRightButton = useMemo(() => {
        return (
            !isTouch &&
            (breakpointXlUp
                ? ipicCount > Constants.IPIC_SLIDER_VISIBLE_XLARGE &&
                  index + Constants.IPIC_SLIDER_VISIBLE_XLARGE + 0.5 < ipicCount
                : ipicCount > Constants.IPIC_SLIDER_VISIBLE_LARGE_DESC &&
                  index + Constants.IPIC_SLIDER_VISIBLE_LARGE_DESC + 0.5 < ipicCount)
        );
    }, [isTouch, index, breakpointXlUp, ipicCount]);

    const touchAttributes =
        isTouch && isSlideable()
            ? {
                  onTouchStart: handleTouchStart,
                  onTouchEnd: handleTouchEnd,
                  onTouchMove: handleTouchMove,
              }
            : {};

    return (
        <div className="ipic__slider-wrapper">
            <div className="ipic__slider-content-wrapper" {...touchAttributes}>
                <div
                    ref={sliderRef}
                    className="ipic__slider-content"
                    style={{transform: `translateX(${currentXPosition}px)`}}
                >
                    {firstViewColors.map((color, index) => (
                        <div
                            key={index}
                            className="alpi__color-slide-wrapper"
                            jsh-color-slide-wrapper=""
                            onClick={() => handleSlideClick(index)}
                        >
                            <Ipic
                                color={color}
                                index={index}
                                activeIndex={activeIndex}
                                shopId={shopId}
                                channelId={channelId}
                                onChangeItem={onChangeItem}
                                inView={inView}
                            />
                        </div>
                    ))}
                </div>
            </div>
            {isLeftButton && (
                <SymbolButton
                    className="ipic__slider-button prev"
                    icon="arrow-left"
                    attributes={{'aria-label': texts["prevPage"]}}
                    onClick={() => onSliderButtonClick(false)}
                />
            )}
            {isRightButton && (
                <SymbolButton
                    className="ipic__slider-button next"
                    icon="arrow-right"
                    attributes={{'aria-label': texts["nextPage"]}}
                    onClick={() => onSliderButtonClick(true)}
                />
            )}
        </div>
    );

    function onSliderButtonClick(isSlideRight) {
        isSlideRight
            ? slideRight(index, Constants.IPIC_SLIDER_SLIDE_STEP_XLARGE, Constants.IPIC_SLIDER_SLIDE_STEP_LARGE_DESC)
            : slideLeft(index, Constants.IPIC_SLIDER_SLIDE_STEP_XLARGE, Constants.IPIC_SLIDER_SLIDE_STEP_LARGE_DESC);
    }

    function handleTouchStart(e) {
        setTouchPositionX(e.touches[0].clientX);
        currentSlidePosition.current = currentXPosition;
    }

    function handleTouchMove(e) {
        if (isSlideable()) {
            document.body.classList.add('disable-large-scrollbar');
            const currentTouch = e.touches[0].clientX;
            const diff = touchPositionX - currentTouch;
            sliderRef.current.style.transform = `translateX(${-diff + currentXPosition}px)`;
            currentSlidePosition.current = -diff + currentXPosition;
        }
    }

    function handleTouchEnd(e) {
        document.body.classList.remove('disable-large-scrollbar');
        const closestIPicIndex = -Math.round(currentSlidePosition.current / getAlpiColorSlideWidth());
        if (touchPositionX > e.changedTouches[0].clientX) {
            slideRight(closestIPicIndex);
        } else if (touchPositionX < e.changedTouches[0].clientX) {
            slideLeft(closestIPicIndex);
        }
    }

    // a slide to the right needs to bounce back to the last ipic if boundary of the slider was reached
    function slideRight(indexToCheck, stepXL = 0, stepLDESC = 0) {
        const newIndex = breakpointXlUp
            ? Math.min(
                  ipicCount - (Constants.IPIC_SLIDER_VISIBLE_XLARGE + 0.5),
                  Math.min(ipicCount, indexToCheck + stepXL)
              )
            : Math.min(
                  ipicCount - (Constants.IPIC_SLIDER_VISIBLE_LARGE_DESC + 0.5),
                  Math.min(ipicCount, indexToCheck + stepLDESC)
              );
        lockInSliderPosition(newIndex);
    }

    // a slide to the left needs to bounce back to the first ipic if boundary of the slider was reached
    function slideLeft(indexToCheck, stepXL = 0, stepLDESC = 0) {
        const newIndex = breakpointXlUp ? Math.max(0, indexToCheck - stepXL) : Math.max(0, indexToCheck - stepLDESC);
        lockInSliderPosition(newIndex);
    }

    // adjust the slider position to the start of the currently touched ipic
    function lockInSliderPosition(newIndex) {
        if (newIndex === index) {
            sliderRef.current.style.transform = `translateX(${currentXPosition}px)`;
        } else {
            setIndex(newIndex);
        }
    }

    function handleSlideClick(clickedIndex) {
        const cutOffLeft = clickedIndex < index;
        const visibleSlides = breakpointXlUp
            ? Constants.IPIC_SLIDER_VISIBLE_XLARGE
            : Constants.IPIC_SLIDER_VISIBLE_LARGE_DESC;
        const cutOffRight = clickedIndex === index + visibleSlides;
        if (cutOffLeft) {
            setIndex(clickedIndex);
        } else if (cutOffRight) {
            const isLastSlide = clickedIndex === ipicCount - 1;
            if (isLastSlide) {
                setIndex(index + 0.5);
            } else {
                setIndex(index + 1);
            }
        }
    }

    function getAlpiColorSlideWidth() {
        const element = sliderRef.current.querySelectorAll('[jsh-color-slide-wrapper]')[1]; //get the second element because the first one has no predecessor and therefore no margin

        if (element) {
            const style = element.currentStyle || window.getComputedStyle(element);
            return parseFloat(style.width) + parseFloat(style.marginLeft); // add margin to get the full shift value
        }
        return 0;
    }

    function getAlpiColorSlideMargin() {
        const element = sliderRef.current.querySelectorAll('[jsh-color-slide-wrapper]')[1];

        if (element) {
            const style = element.currentStyle || window.getComputedStyle(element);
            return parseFloat(style.marginLeft);
        }
        return 0;
    }

    function isSlideable() {
        return breakpointXlUp
            ? ipicCount > Constants.IPIC_SLIDER_VISIBLE_XLARGE
            : ipicCount > Constants.IPIC_SLIDER_VISIBLE_LARGE_DESC;
    }
}

export default React.memo(AlpiIpicSlider);
